import {
  FormatterOptions,
  numberFormatter,
  postfixFormatter,
} from '../../../components/table/valueFormatters';

import { resolveNumberInputBounds } from './numberInputBounds';
import { NumberBoundsSchema } from './numberSchema';

type NumberExampleOptions = Pick<
  FormatterOptions,
  'digits' | 'disableThousandsSeparator' | 'postfix'
>;

/**
 * Picks an example value for number placeholders in display space
 * (same space as NumberInput values and schema bounds).
 * Uses the midpoint when both bounds exist,
 * the defined bound when only min or max is set, otherwise `1`.
 */
export function getNumberExampleValue(
  schema: NumberBoundsSchema,
  options: NumberExampleOptions
): number {
  const { min, max } = resolveNumberInputBounds(
    schema,
    options.digits ?? undefined
  );

  if (min != null && max != null) {
    return (min + max) / 2;
  }
  if (min != null) {
    return min;
  }
  if (max != null) {
    return max;
  }

  return 1;
}

/**
 * Formats a locale-aware example via number/postfix formatters, e.g. "50%".
 * Factor is omitted because the example is already in display space.
 */
export function formatNumberPlaceholderExample(
  schema: NumberBoundsSchema,
  options: NumberExampleOptions & Pick<FormatterOptions, 'factor'>
): string | undefined {
  const value = getNumberExampleValue(schema, options);
  const formattedExample = postfixFormatter(
    numberFormatter(value, {
      ...options,
      // Factor is omitted because the example is already in display space.
      factor: undefined,
    }),
    options
  );

  if (formattedExample == null || formattedExample === '') {
    return undefined;
  }

  return String(formattedExample);
}
